import { useEffect, useRef, useState, type RefObject } from "react";
import mapboxgl, { Popup } from "mapbox-gl";
import type { Map as MapboxMap, MapMouseEvent } from "mapbox-gl";
import PropertyDetail from "./PropertyDetail";

interface PropertyProps {
    map: RefObject<MapboxMap | undefined>;
}

const UPRN_SOURCE = "uprn-points-source";
const UPRN_LAYER = "uprn-points";

function attachUprnLayer(map: MapboxMap) {
    if (map.getSource(UPRN_SOURCE)) return;
    map.addSource(UPRN_SOURCE, {
        type: "vector",
        tiles: [
            `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/uprn-points/{z}/{x}/{y}`,
        ],
        minzoom: 15,
    });
    map.addLayer({
        id: UPRN_LAYER,
        type: "circle",
        source: UPRN_SOURCE,
        "source-layer": "uprn_points",
        minzoom: 15,
        paint: {
            "circle-radius": [
                "interpolate",
                ["linear"],
                ["zoom"],
                15,
                2,
                18,
                4,
                20,
                6,
            ],
            "circle-color": "#ffe066",
            "circle-stroke-color": "#000",
            "circle-stroke-width": 1,
            "circle-opacity": 0.9,
        },
    });
}

function detachUprnLayer(map: MapboxMap) {
    if (map.getLayer(UPRN_LAYER)) map.removeLayer(UPRN_LAYER);
    if (map.getSource(UPRN_SOURCE)) map.removeSource(UPRN_SOURCE);
}

function Property(props: PropertyProps) {
    const [selectedProperty, setSelectedProperty] = useState<number>();
    const [showUprns, setShowUprns] = useState(false);

    const uprnEnterRef = useRef<((e: MapMouseEvent) => void) | null>(null);
    const uprnLeaveRef = useRef<(() => void) | null>(null);
    const uprnMoveRef = useRef<((e: MapMouseEvent) => void) | null>(null);
    const uprnClickRef = useRef<((e: MapMouseEvent) => void) | null>(null);
    const popupRef = useRef<Popup | null>(null);
    const selectedFeatureIdRef = useRef<number | null>(null);
    // Ref so the tile useEffect can read the current showUprns without re-running.
    const showUprnsRef = useRef(showUprns);

    useEffect(() => {
        showUprnsRef.current = showUprns;
    }, [showUprns]);

    function detachUprnHover(map: MapboxMap) {
        if (uprnEnterRef.current) {
            map.off("mouseenter", UPRN_LAYER, uprnEnterRef.current);
            uprnEnterRef.current = null;
        }
        if (uprnMoveRef.current) {
            map.off("mousemove", UPRN_LAYER, uprnMoveRef.current);
            uprnMoveRef.current = null;
        }
        if (uprnLeaveRef.current) {
            map.off("mouseleave", UPRN_LAYER, uprnLeaveRef.current);
            uprnLeaveRef.current = null;
        }
        if (uprnClickRef.current) {
            map.off("click", UPRN_LAYER, uprnClickRef.current);
            uprnClickRef.current = null;
        }
        if (popupRef.current) {
            popupRef.current.remove();
            popupRef.current = null;
        }
    }

    function attachUprnHover(map: MapboxMap) {
        const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 8,
            className: "uprn-hover-popup",
        });

        const onEnter = (e: MapMouseEvent) => {
            map.getCanvas().style.cursor = "crosshair";
            const f = (
                e as unknown as { features?: mapboxgl.MapboxGeoJSONFeature[] }
            ).features?.[0];
            if (!f) return;
            const uprn = f.properties?.uprn;
            popup
                .setLngLat(e.lngLat)
                .setHTML(
                    `<div class="uprn-hover-popup__body">UPRN ${uprn}</div>`,
                )
                .addTo(map);
            popupRef.current = popup;
        };
        const onMove = (e: MapMouseEvent) => {
            const f = (
                e as unknown as { features?: mapboxgl.MapboxGeoJSONFeature[] }
            ).features?.[0];
            if (!f) return;
            const uprn = f.properties?.uprn;
            popup
                .setLngLat(e.lngLat)
                .setHTML(
                    `<div class="uprn-hover-popup__body">UPRN ${uprn}</div>`,
                );
        };
        const onLeave = () => {
            map.getCanvas().style.cursor = "";
            popup.remove();
        };
        const onClick = (e: MapMouseEvent) => {
            const f = (
                e as unknown as { features?: mapboxgl.MapboxGeoJSONFeature[] }
            ).features?.[0];
            if (!f) return;
            const uprn = String(f.properties?.uprn ?? "");
            if (!uprn) return;
            navigator.clipboard?.writeText(uprn).catch(() => {});
            popup
                .setLngLat(e.lngLat)
                .setHTML(
                    `<div class="uprn-hover-popup__body uprn-hover-popup__body--copied">UPRN ${uprn} · copied</div>`,
                )
                .addTo(map);
            popupRef.current = popup;
        };

        map.on("mouseenter", UPRN_LAYER, onEnter);
        map.on("mousemove", UPRN_LAYER, onMove);
        map.on("mouseleave", UPRN_LAYER, onLeave);
        map.on("click", UPRN_LAYER, onClick);
        uprnEnterRef.current = onEnter;
        uprnMoveRef.current = onMove;
        uprnLeaveRef.current = onLeave;
        uprnClickRef.current = onClick;
    }

    function setUprnsVisible(visible: boolean) {
        setShowUprns(visible);
        const map = props.map.current;
        if (!map) return;
        if (visible) {
            attachUprnLayer(map);
            attachUprnHover(map);
        } else {
            detachUprnHover(map);
            detachUprnLayer(map);
        }
    }

    // Mount the single, unified property-geometry layer. showUprns is read via
    // ref to avoid recreating this layer on UPRN toggle.
    useEffect(() => {
        const map = props.map.current;
        if (!map) return;

        setSelectedProperty(undefined);

        map.addSource("mvt-tiles", {
            type: "vector",
            tiles: [
                `${process.env.BUN_PUBLIC_TILE_SERVER}/api/tiles/property-geometry/{z}/{x}/{y}`,
            ],
            minzoom: 14,
        });
        map.addLayer({
            id: "tiles",
            type: "fill",
            source: "mvt-tiles",
            "source-layer": "property_geometry",
            paint: {
                "fill-color": "rgba(255, 85, 31, 1)",
                "fill-opacity": [
                    "case",
                    ["==", ["to-number", ["get", "has_uprn"]], 0],
                    0.1,
                    [">", ["to-number", ["get", "has_epc"]], 0],
                    1,
                    0.4,
                ],
                "fill-outline-color": "rgba(255, 255, 255, 0.5)",
            },
        });
        map.addLayer({
            id: "tiles-selected-outline",
            type: "line",
            source: "mvt-tiles",
            "source-layer": "property_geometry",
            paint: {
                "line-color": "#ffffff",
                "line-width": [
                    "case",
                    ["boolean", ["feature-state", "selected"], false],
                    3,
                    0,
                ],
            },
        });

        const clickHandler = (e: MapMouseEvent) => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: ["tiles"],
            });
            const f = features[0];
            if (!f) return;
            const id = Number(f.id ?? f.properties?.id);
            if (Number.isNaN(id)) return;
            setSelectedProperty(id);
        };
        const enterHandler = () => {
            map.getCanvas().style.cursor = "pointer";
        };
        const leaveHandler = () => {
            map.getCanvas().style.cursor = "";
        };

        map.on("click", "tiles", clickHandler);
        map.on("mouseenter", "tiles", enterHandler);
        map.on("mouseleave", "tiles", leaveHandler);

        if (showUprnsRef.current && map.getLayer(UPRN_LAYER)) {
            map.moveLayer(UPRN_LAYER);
        }

        return () => {
            map.off("click", "tiles", clickHandler);
            map.off("mouseenter", "tiles", enterHandler);
            map.off("mouseleave", "tiles", leaveHandler);
            map.getCanvas().style.cursor = "";
            selectedFeatureIdRef.current = null;
            if (map.getLayer("tiles-selected-outline"))
                map.removeLayer("tiles-selected-outline");
            if (map.getLayer("tiles")) map.removeLayer("tiles");
            if (map.getSource("mvt-tiles")) map.removeSource("mvt-tiles");
        };
    }, [props.map]);

    // Keep the map's feature-state in sync with the panel's selection, so the
    // highlighted polygon tracks selectedProperty regardless of whether it
    // changed via a map click or via the detail panel's back/close button.
    useEffect(() => {
        const map = props.map.current;
        if (!map || !map.getSource("mvt-tiles")) return;

        if (selectedFeatureIdRef.current != null) {
            map.setFeatureState(
                {
                    source: "mvt-tiles",
                    sourceLayer: "property_geometry",
                    id: selectedFeatureIdRef.current,
                },
                { selected: false },
            );
        }
        selectedFeatureIdRef.current = selectedProperty ?? null;
        if (selectedProperty != null) {
            map.setFeatureState(
                {
                    source: "mvt-tiles",
                    sourceLayer: "property_geometry",
                    id: selectedProperty,
                },
                { selected: true },
            );
        }
    }, [props.map, selectedProperty]);

    // UPRN layer cleanup on unmount (tab switch).
    useEffect(() => {
        return () => {
            const map = props.map.current;
            if (map) {
                detachUprnHover(map);
                detachUprnLayer(map);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="tiles-tab">
            <div className="tiles-tab__content">
                {selectedProperty != null ? (
                    <PropertyDetail
                        propertyId={selectedProperty}
                        onClose={() => setSelectedProperty(undefined)}
                    />
                ) : (
                    <div
                        style={{
                            fontSize: 12,

                            padding: "24px 16px",
                            fontFamily: "monospace",
                        }}
                    >
                        Zoom in to see Properties. Click a polygon for details.
                    </div>
                )}
            </div>
            <label className="tiles-tab__footer">
                <input
                    type="checkbox"
                    checked={showUprns}
                    onChange={(e) => setUprnsVisible(e.target.checked)}
                />
                Show UPRNs (zoom ≥ 15)
            </label>
        </div>
    );
}

export default Property;
